import type { RequestFn } from '../http';
import type { WeclappTicketIntakeType } from './weclapp';
import type { KianUsage, WithKianUsage } from '../types/core';

export type ChatConversationStatus = 'OPEN' | 'CLOSED';
export type ChatMessageSource = 'CUSTOMER' | 'TECHNICIAN' | 'AUTO_NOTIFY' | 'KIAN' | 'EXTERNAL';

export interface ChatUserRef {
  id: string;
  displayName: string;
}

export interface ChatTicketLink {
  id: string;
  weclappTicketId: string;
  ticketNumber: string | null;
  createdAt: string;
}

export interface ChatReplyWindow {
  /** true = freie Antworten erlaubt (24h nach letzter Kundennachricht). */
  open: boolean;
  expiresAt: string | null;
}

export interface ChatCustomerSuggestion {
  customer: { id: string; name: string };
  confidence: number | null;
  reasoning: string | null;
}

/** Chat-Anbieter hinter der Inbox (CHAT_PROVIDER am Server). */
export type ChatProviderName = 'chatwerk' | 'meta';

export interface ChatConversation {
  id: string;
  /** Leer bei Konversationen, die erst über die WhatsApp Cloud API entstanden sind. */
  chatwerkConversationId: string | null;
  provider?: 'CHATWERK' | 'META';
  /** WhatsApp-Benutzername — bei Kunden ohne sichtbare Rufnummer der einzige Anhaltspunkt. */
  contactUsername?: string | null;
  messengerType: string | null;
  contactName: string | null;
  contactPhoneE164: string | null;
  customer: { id: string; name: string; isVip: boolean; weclappCustomerId: string | null } | null;
  customerContact: { id: string; name: string | null } | null;
  matchType: string | null;
  /** KIAN-Kundenvorschlag (Confidence unter der Auto-Schwelle) — wartet auf Bestätigung. */
  suggestion: ChatCustomerSuggestion | null;
  assignedTo: ChatUserRef | null;
  /** KIAN hat die Zuweisung nachts aufgehoben (Thema erledigt/verlagert). */
  assignmentRelease?: { at: string; reason: string | null; from: ChatUserRef | null } | null;
  status: ChatConversationStatus;
  unreadCount: number;
  lastInboundAt: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  /** Nur in Listen: erste Kundennachricht seit der letzten Antwort — null, wenn beantwortet. */
  waitingSince?: string | null;
  ticketLinks: ChatTicketLink[];
  replyWindow: ChatReplyWindow;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  direction: 'in' | 'out';
  type: string;
  text: string | null;
  media: unknown;
  status: string | null;
  errorMessage: string | null;
  /** STT-Transkript einer Sprachnachricht (serverseitig gecacht). */
  transcription: string | null;
  source: ChatMessageSource;
  author: ChatUserRef | null;
  sentAt: string;
  /** Reaktionen des Kunden (Meta) — hängen an der Nachricht, keine eigene Blase. */
  reactions?: { emoji: string; by: 'customer' | 'business'; at: string }[] | null;
  /** Antwort auf eine frühere Nachricht (Meta: context.id). */
  replyTo?: { id: string; direction: 'in' | 'out'; text: string } | null;
}

/** Freigegebenes WhatsApp-Template aus dem Meta-Katalog. */
export interface ChatTemplate {
  name: string;
  language: string;
  category: string | null;
  parameterFormat: 'NAMED' | 'POSITIONAL';
  headerText: string | null;
  bodyText: string;
  footerText: string | null;
  /** key = Name (NAMED) oder „1", „2" … (POSITIONAL); location = header | body | button:<n> */
  placeholders: { key: string; location: string }[];
  sendable: boolean;
  unsupportedReason: string | null;
}

/** Tabs wie in Chatwerk: Neu / Gelesen / Unbeantwortet / Erledigt / Alle (`active` = Alt-Wert, alle offenen). */
export type ChatBucket = 'unread' | 'read' | 'active' | 'unanswered' | 'archived' | 'all';

export interface ChatsListParams {
  status?: 'open' | 'closed' | 'all';
  /** Inbox-Tabs: Ungelesen / In Bearbeitung (alle offenen) / Archiv. Übersteuert status. */
  bucket?: ChatBucket;
  assignee?: 'me' | 'unassigned' | string;
  linked?: 'with' | 'without';
  unreadOnly?: boolean;
  q?: string;
  limit?: number;
}

export interface ChatsStats {
  open: number;
  withUnread: number;
  unassigned: number;
  archived: number;
  /** Offene Chats, in denen der Kunde das letzte Wort hat (Auto-Nachrichten zählen nicht). */
  unanswered?: number;
}

export function createChatsApi(request: RequestFn) {
  return {
    /**
     * Bestehende Konversation zu Kunde/Rufnummer — für den Sprung aus dem Ticket
     * in den WhatsApp-Chat. `null`, wenn es noch keine gibt (das Portal kann keine
     * neue Konversation eröffnen).
     */
    async lookupChatConversation(params: {
      weclappPartyId?: string;
      phone?: string;
      ticketNumber?: string;
    }) {
      const qs = new URLSearchParams();
      if (params.weclappPartyId) qs.set('weclappPartyId', params.weclappPartyId);
      if (params.phone) qs.set('phone', params.phone);
      if (params.ticketNumber) qs.set('ticketNumber', params.ticketNumber);
      return request<{
        /**
         * wa.me-Einladung auf unsere eigene Nummer. Steht auch ohne bestehende
         * Konversation bereit — eröffnen kann sie nur der Kunde. Der QR-Code dazu
         * entsteht im Browser und wird als data:-URI in den Kommentar eingebettet.
         */
        whatsappInvite: { link: string } | null;
        conversation: {
          id: string;
          contactName: string | null;
          messengerType: string | null;
          archived: boolean;
          /** 'customer' = Chat des Unternehmens, nicht zwingend der des Melders. */
          matchedBy: 'phone' | 'customer';
          replyWindowOpen: boolean;
        } | null;
      }>(`/chats/lookup?${qs.toString()}`);
    },

    async getChats(params: ChatsListParams = {}) {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.bucket) qs.set('bucket', params.bucket);
      if (params.assignee) qs.set('assignee', params.assignee);
      if (params.linked) qs.set('linked', params.linked);
      if (params.unreadOnly) qs.set('unreadOnly', 'true');
      if (params.q) qs.set('q', params.q);
      if (params.limit) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<{
        configured: boolean;
        provider?: ChatProviderName;
        /** Vorlagen (Meta-Katalog) wählbar — nur mit WhatsApp Cloud API. */
        templatesAvailable?: boolean;
        conversations: ChatConversation[];
      }>(
        `/chats${query ? `?${query}` : ''}`,
      );
    },

    async getChatsStats() {
      return request<ChatsStats>('/chats/stats');
    },

    async getChat(id: string) {
      return request<{ conversation: ChatConversation; messages: ChatMessage[] }>(`/chats/${id}`);
    },

    /**
     * Antwort senden. Mit Bildern geht der Aufruf als multipart raus — der Text
     * wird dann zur Bildunterschrift des ersten Bildes (WhatsApp-Verhalten).
     */
    async sendChatMessage(id: string, text: string, images: File[] = []) {
      if (images.length === 0) {
        return request<{ ok: boolean; messageId: string }>(`/chats/${id}/messages`, {
          method: 'POST',
          body: JSON.stringify({ text }),
        });
      }
      const form = new FormData();
      form.append('text', text);
      for (const image of images) form.append('files', image, image.name);
      return request<{ ok: boolean; messageId: string }>(`/chats/${id}/messages`, {
        method: 'POST',
        body: form,
      });
    },

    /** Freigegebene WhatsApp-Templates (leer, solange ChatWerk der Anbieter ist). */
    async getChatTemplates() {
      return request<{ provider: ChatProviderName; templates: ChatTemplate[] }>('/chats/templates');
    },

    /** Template senden — geht auch, wenn das 24-Stunden-Fenster zu ist. */
    async sendChatTemplate(id: string, payload: { name: string; language: string; params: Record<string, string> }) {
      return request<{ ok: boolean; messageId: string }>(`/chats/${id}/template`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    /** Sprachnachricht transkribieren — idempotent, Server cacht das Ergebnis. */
    async transcribeChatMessage(id: string, messageId: string) {
      return request<{ transcription: string; cached: boolean }>(
        `/chats/${id}/messages/${messageId}/transcribe`,
        { method: 'POST' },
      );
    },

    async markChatRead(id: string) {
      return request<{ ok: boolean }>(`/chats/${id}/read`, { method: 'POST' });
    },

    async markChatUnread(id: string) {
      return request<{ ok: boolean }>(`/chats/${id}/unread`, { method: 'POST' });
    },

    async renameChatContact(id: string, name: string) {
      return request<{ conversation: ChatConversation }>(`/chats/${id}/rename`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
    },

    async dismissChatSuggestion(id: string) {
      return request<{ conversation: ChatConversation }>(`/chats/${id}/dismiss-suggestion`, {
        method: 'POST',
      });
    },

    async updateChat(
      id: string,
      payload: {
        assignedToId?: string | null;
        /** Optionale Übergabe-Notiz — landet in der Benachrichtigung an den neuen Bearbeiter. */
        note?: string;
        status?: ChatConversationStatus;
      },
    ) {
      return request<{ conversation: ChatConversation }>(`/chats/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },

    async linkChatCustomer(
      id: string,
      payload: { customerId?: string; weclappCustomerId?: string; customerContactId?: string | null },
    ) {
      return request<{ conversation: ChatConversation }>(`/chats/${id}/link-customer`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    async draftChatReply(id: string, draft?: string) {
      return request<WithKianUsage<{ text: string; refined: boolean }>>(`/chats/${id}/kian-draft`, {
        method: 'POST',
        kianUsageInline: true,
        body: JSON.stringify({ draft }),
      });
    },

    async getChatTicketTopics(id: string) {
      return request<WithKianUsage<ChatTicketTopics>>(`/chats/${id}/ticket-topics`, {
        method: 'POST',
        kianUsageInline: true,
      });
    },

    async getChatSituationAnalysis(id: string) {
      return request<ChatSituationAnalysisStatus>(`/chats/${id}/situation-analysis`);
    },
    async runChatSituationAnalysis(id: string) {
      return request<ChatSituationAnalysisResult>(`/chats/${id}/situation-analysis`, {
        method: 'POST',
        kianUsageInline: true,
      });
    },
    async draftChatTicket(id: string, topics?: string[], forcedType?: WeclappTicketIntakeType) {
      return request<WithKianUsage<ChatTicketDraft>>(`/chats/${id}/ticket-draft`, {
        method: 'POST',
        kianUsageInline: true,
        body: JSON.stringify({
          ...(topics && topics.length > 0 ? { topics } : {}),
          ...(forcedType ? { forcedType } : {}),
        }),
      });
    },

    async createChatTicket(id: string, payload: CreateChatTicketPayload) {
      return request<CreateChatTicketResult>(`/chats/${id}/create-ticket`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    async getChatTickets(id: string) {
      return request<{ tickets: ChatLinkedTicket[] }>(`/chats/${id}/tickets`);
    },

    /** Bild-Nachrichten im Ticket-Fenster — für die Anhang-Auswahl im Ticket-Dialog. */
    async getChatTicketImages(id: string) {
      return request<{ images: ChatTicketWindowImage[]; maxAttach: number }>(
        `/chats/${id}/ticket-images`,
      );
    },

    /** Rückweg Ticket → Chat: zuletzt verknüpfte Konversation zu einem Weclapp-Ticket. */
    async getChatForTicket(weclappTicketId: string) {
      return request<{
        conversationId: string | null;
        contactName?: string | null;
        customerName?: string | null;
      }>(`/chats/for-ticket/${encodeURIComponent(weclappTicketId)}`);
    },
  };
}

export type ChatTicketPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface ChatTicketTopic {
  title: string;
  summary: string | null;
}

export interface ChatTicketTopics {
  topics: ChatTicketTopic[];
  usedMessages: number;
  usedSince: string | null;
  scopedToNewMessages: boolean;
}

export interface ChatTicketDraft {
  /** Gemeinsame Typ-Taxonomie aller Anlage-Wege (Störung/Auftrag/Wartung/Beratung). */
  ticketType: WeclappTicketIntakeType;
  subject: string;
  description: string;
  priority: ChatTicketPriority | null;
  /** KIAN-Fristvorschlag (YYYY-MM-DD), nur wenn der Chat ein klares Zeitsignal enthält. */
  deadline: string | null;
  /** KIAN-Kategorievorschlag — Weclapp-Id, nur wenn exakt in der Live-Liste vorhanden. */
  categoryId: string | null;
  /** Wer das Anliegen laut Chat gemeldet hat (KIAN-Extraktion) — null wenn unklar. */
  reportedBy: string | null;
  /** Gemeldet-von, gematcht gegen die Weclapp-Ansprechpartner des Kunden. */
  suggestedContactId: string | null;
  suggestedContactName: string | null;
  /** Transparenz: was KIAN gelesen hat. */
  usedMessages: number;
  usedSince: string | null;
  /** true = nur Nachrichten seit der letzten Ticket-Verknüpfung. */
  scopedToNewMessages: boolean;
}

export interface CreateChatTicketPayload {
  subject: string;
  description?: string;
  priority?: ChatTicketPriority | null;
  /** Frist (YYYY-MM-DD): Block in der Beschreibung + natives Weclapp-dueDate. */
  deadline?: string | null;
  /** Ticket-Kategorie (Weclapp-Id aus der Live-Liste). */
  categoryId?: string | null;
  /** Pflicht, wenn die Konversation keinen gematchten Kunden hat. */
  weclappCustomerId?: string;
  /** Ansprechpartner (Weclapp-Kontakt-Id) — KIAN-Vorschlag oder Techniker-Wahl. */
  weclappContactId?: string | null;
  /** Wer das Anliegen laut Chat gemeldet hat — landet als Zeile in der Ticket-Beschreibung. */
  reportedBy?: string | null;
  sendConfirmation?: boolean;
  /** Chat-Bilder (Message-Ids aus dem Ticket-Fenster), die als Anhang ans Ticket gehen. */
  attachMessageIds?: string[];
  /** Bearbeiter (Weclapp-User-Id) — leer = Ticket bleibt im Pool. */
  assignedWeclappUserId?: string | null;
}

export interface ChatTicketWindowImage {
  messageId: string;
  filename: string;
  mimeType: string;
  sentAt: string;
  direction: string;
  /** Bild-Unterschrift aus dem Chat, falls vorhanden. */
  caption: string | null;
}

export interface ChatLinkedTicket {
  id: string;
  weclappTicketId: string;
  ticketNumber: string | null;
  subject: string | null;
  status: string | null;
  contactName: string | null;
  active: boolean;
  createdAt: string;
  createdBy: ChatUserRef | null;
}

export interface CreateChatTicketResult {
  ok: boolean;
  ticketId: string;
  ticketNumber: string | null;
  ticketLink?: string;
  provider: 'connectwise' | 'weclapp';
  confirmationSent: boolean;
  /** Anzahl der Chat-Bilder, die als Anhang am Ticket gelandet sind. */
  attachedImages: number;
  /** Gewählte Bilder, die nicht angehängt werden konnten (abgelaufen/zu groß/Upload-Fehler). */
  failedImages: number;
  /** Bearbeiter wurde gesetzt. */
  assigned?: boolean;
  /** Ticket steht, Zuweisung ist gescheitert — Grund für den Hinweis. */
  assignError?: string | null;
}

export interface ChatSituationAnalysisResult {
  id: string;
  analysisText: string;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
  /** Modell, Tokens, Kosten — null bei älteren Analysen */
  llmUsage: KianUsage | null;
}

export interface ChatSituationAnalysisStatus {
  latest: ChatSituationAnalysisResult | null;
  newMessagesSince: number;
}
